export const MANDATORY_METRICS = ['impression', 'click'] as const

export const METRIC_LABELS: Record<string, string> = {
  impression: 'Impressions',
  click: 'Clicks',
  ctr: 'CTR',
}

export function formatMetricLabel(key: string): string {
  return (
    METRIC_LABELS[key] ??
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

const BASE_COLORS: Record<string, string> = {
  impression: '#3b82f6',
  click: '#10b981',
  ctr: '#8b5cf6',
}

const FALLBACK_PALETTE = ['#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16']

export function getMetricColor(key: string, index: number): string {
  return BASE_COLORS[key] ?? FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]
}

export interface DerivedMetricDef {
  key: string
  label: string
  /** Raw metric keys required to compute this metric */
  requires: string[]
  compute: (vals: Record<string, number>) => number
  formatValue: (value: number) => string
  /** True when Y-axis should use % suffix */
  isPercentage?: boolean
}

export const DERIVED_METRICS: DerivedMetricDef[] = [
  {
    key: 'ctr',
    label: 'CTR',
    requires: ['impression', 'click'],
    compute: (vals) =>
      vals.impression > 0
        ? parseFloat(((vals.click / vals.impression) * 100).toFixed(3))
        : 0,
    formatValue: (v) => `${v.toFixed(2)}%`,
    isPercentage: true,
  },
  // Add future derived metrics here, e.g.:
  // { key: 'cpm', label: 'CPM', requires: ['impression', 'spend'], compute: ..., formatValue: ... },
]

export function getDerivedMetric(key: string): DerivedMetricDef | undefined {
  return DERIVED_METRICS.find((m) => m.key === key)
}

export function isDerivedMetric(key: string): boolean {
  return DERIVED_METRICS.some((m) => m.key === key)
}

export function isMandatoryMetric(key: string): boolean {
  return (MANDATORY_METRICS as readonly string[]).includes(key)
}

/** Safe numeric accessor for a row with dynamic metric keys */
export function getMetricValue(
  row: Record<string, number | string | undefined>,
  key: string,
): number {
  return Number(row[key]) || 0
}
